import { includes } from "lodash";
import { useRouter } from "next/router";
import { confirmDialog } from "../../../../common/utils/dialog.utils";
import { Trans, useTranslation } from "react-i18next";
import usePermissions from "../../../../common/api/usePermissions";
import { COMPANY_ROUTES, PERMISSIONS } from "../../../../common/constants";
import { useGetIntegrationsStatus } from "../../api/useIntegrationsStatus";
import { INTEGRATION_TYPES } from "../../constants";
import { IntegrationStatus } from "../../models";

export const useMissingIntegration = () => {
    const router = useRouter();
    const { t } = useTranslation(["devices", "common"]);
    const { hasPermissions } = usePermissions();

    const { triggerGetIntegrationsStatus } = useGetIntegrationsStatus({});

    const showMissingIntegrationModal = async ({
        companyId,
        locationId,
        onIntegrationExists = () => {},
        onIntegrationMissing = () => {},
    }: {
        companyId?: string;
        locationId?: string;
        onIntegrationExists?: () => void;
        onIntegrationMissing?: () => void;
    }) => {
        const integrationsStatus: IntegrationStatus =
            await triggerGetIntegrationsStatus({ companyId, locationId });

        const isMissing = !includes(
            integrationsStatus?.activeIntegrationTypes,
            INTEGRATION_TYPES.LNS
        );

        if (!isMissing || !companyId) {
            onIntegrationExists();
            return;
        }

        onIntegrationMissing();

        const canCreateIntegration = hasPermissions({
            permissions: [PERMISSIONS.INTEGRATION.CREATE],
            companyId,
        });

        confirmDialog({
            header: t("assignDeviceToSpace.missingIntegration"),
            message: (
                <Trans
                    i18nKey={`devices:assignDeviceToSpace.${
                        canCreateIntegration
                            ? "setUpIntegrationFirst"
                            : "contactYourAdmin"
                    }`}
                />
            ),
            pt: { content: { className: "*:m-0!" } },
            acceptClassName: canCreateIntegration ? "" : "hidden",
            rejectClassName: canCreateIntegration
                ? "p-button-secondary p-button-text"
                : "hidden",
            rejectLabel: t("common:cancel") || "",
            acceptLabel: t("assignDeviceToSpace.setupIntegration") || "",
            accept: () =>
                router.push({
                    pathname: COMPANY_ROUTES.INTEGRATION,
                    query: { companyId },
                }),
        });
    };

    return {
        showMissingIntegrationModal,
    };
};
